import { useState, useCallback } from "react";
import { useCartContext } from "@/components/cart/cart-provider";
import { getGuestId } from "@/lib/guest";
import { fetchCartLines } from "@/lib/cart/fetch-cart-lines";

export function useAddToCart(userId: string | null) {
  const { addItem, updateShippingMethod } = useCartContext();
  const [adding, setAdding] = useState(false);
  const [justAdded, setJustAdded] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const clearAddError = useCallback(() => setAddError(null), []);

  const addToCart = async (listingData: {
    listingId: string;
    name: string;
    price: number;
    currency?: string | null;
    images: string[];
    sellerId: string;
    sellerName: string;
    sellerAvatarUrl?: string;
    condition: string;
    description: string;
    shippingMethods?: any[];
    maxQuantity: number;
  }, opts?: { selectedShippingId?: string | null; roomName?: string | null; messageId?: string | null }) => {
    if (adding) return;
    setAdding(true);
    setAddError(null);

    try {
      await addItem(listingData, { roomName: opts?.roomName, messageId: opts?.messageId });

      setJustAdded(true);
      setTimeout(() => setJustAdded(false), 2000);

      // Set pre-selected shipping method (chat flow)
      if (opts?.selectedShippingId) {
        try {
          const gid = getGuestId();
          const rows = await fetchCartLines({ userId, guestId: gid });
          const found = rows.find(
            (r) =>
              r.listing_id === listingData.listingId ||
              r.listings?.id === listingData.listingId,
          );
          const cartItemId = found?.id ? String(found.id) : null;
          if (cartItemId) {
            await updateShippingMethod(cartItemId, opts.selectedShippingId);
          }
        } catch {}
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to add to cart.";
      setAddError(message);
      console.error("Failed to add to cart:", error);
      throw error;
    } finally {
      setAdding(false);
    }
  };

  return {
    addToCart,
    adding,
    justAdded,
    addError,
    clearAddError,
  };
}
